import { Button } from "@components";
import { getLocalTimeZone, today } from "@internationalized/date";
import { InfoCircle, Route } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Key } from "react-aria";
import type { DateValue } from "react-aria-components";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { Slider } from "@/components/base/slider/slider";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { FILTER_OPTIONS, TRIPS_PAGE_SIZE } from "../constants/tracking";
import { useDebounce, useRefreshPositionPoints, useTrip } from "../hooks";
import type {
	PositionsTripsModalProps,
	RouteCoordinate,
	TripRouteData,
	TripsListProps,
} from "../types";
import { getTrackingPaginationItems } from "../utils";
import {
	adjustTimeRangeForMinInterval,
	formatDateWithTime,
	formatDateWithTimeIso,
	formatHour,
	roundToFiveMinutes,
} from "../utils/tracking.utils";
import { TripCard } from "./cards";
import { PaginationEllipsisDropdown } from "./pagination-ellipsis-dropdown";
import { PositionMetricsView } from "./position-metrics-view";
import { SideModal } from "./side-modal";
import { TimelineStepper } from "./timeline-stepper";

const TripsList = ({
	trips,
	selectedIndex,
	onSelect,
	onTripClick,
}: TripsListProps) => (
	<div className="flex min-w-0 gap-3">
		<TimelineStepper
			count={trips.length}
			onSelect={onSelect}
			selectedIndex={selectedIndex}
		/>
		<div className="flex min-w-0 flex-1 flex-col gap-3">
			{trips.map((trip, index) => (
				<TripCard
					data={trip}
					index={index}
					isSelected={index === selectedIndex}
					key={trip.id}
					onClick={() => onTripClick(trip.id, index)}
				/>
			))}
		</div>
	</div>
);

const LoadingState = () => (
	<div className="flex flex-1 items-center justify-center p-8">
		<p className="text-gray-500 text-sm">Carregando...</p>
	</div>
);

const EmptyState = () => (
	<div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
		<FeaturedIcon color="warning" icon={InfoCircle} size="xl" theme="light" />
		<div className="flex flex-col gap-1">
			<p className="font-medium text-gray-900 text-sm">
				Nenhum trajeto encontrado
			</p>
			<p className="text-gray-500 text-xs">
				Parece que nenhuma corrida foi realizada nesse período.
				<br />
				Tente selecionar um outro horário ou dia.
			</p>
		</div>
	</div>
);

export const PositionsTripsModal = ({
	isOpen,
	onClose,
	vehiclePlate,
	vehicleId,
	onRouteSelect,
	initialTripIds,
}: PositionsTripsModalProps) => {
	const [selectedTab, setSelectedTab] = useState<"trips" | "positions">(
		"positions"
	);
	const [selectedIndex, setSelectedIndex] = useState<number>(-1);
	const [selectedDate, setSelectedDate] = useState<DateValue | null>(
		today(getLocalTimeZone())
	);
	const [timeRange, setTimeRange] = useState<[number, number]>([0, 23.99]);
	const [currentPage, setCurrentPage] = useState(1);
	const previousDebouncedTimeRangeRef = useRef<[number, number] | null>(null);

	const debouncedTimeRange = useDebounce(timeRange, 300);
	const isTripsMode = selectedTab === "trips";
	const hasInitialTripIds = (initialTripIds?.length ?? 0) > 0;

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		setSelectedTab(hasInitialTripIds ? "trips" : "positions");
	}, [isOpen, hasInitialTripIds]);

	const buildParams = useCallback(
		(page: number) => ({
			vehicleId,
			PageNumber: page,
			PageSize: TRIPS_PAGE_SIZE,
			InitialDate: hasInitialTripIds
				? undefined
				: formatDateWithTime(selectedDate, debouncedTimeRange[0]),
			FinalDate: hasInitialTripIds
				? undefined
				: formatDateWithTime(selectedDate, debouncedTimeRange[1]),
			Ids: hasInitialTripIds ? initialTripIds : undefined,
		}),
		[
			vehicleId,
			selectedDate,
			debouncedTimeRange,
			hasInitialTripIds,
			initialTripIds,
		]
	);

	const {
		data: tripsData,
		isLoading: isLoadingTrips,
		isFetching: isFetchingTrips,
	} = useTrip(
		vehicleId,
		isTripsMode && vehicleId > 0 ? buildParams(currentPage) : undefined,
		isTripsMode && vehicleId > 0
	);

	const metricsInitialDate = formatDateWithTimeIso(
		selectedDate,
		debouncedTimeRange[0]
	);
	const metricsFinalDate = formatDateWithTimeIso(
		selectedDate,
		debouncedTimeRange[1]
	);

	const { mutateAsync: getPoints } = useRefreshPositionPoints();

	const handleTimeRangeChange = useCallback((value: number | number[]) => {
		const rawValue = Array.isArray(value) ? value : [value];
		const newValue: [number, number] = [
			roundToFiveMinutes(rawValue[0]),
			roundToFiveMinutes(rawValue[1]),
		];
		const adjustedValue = adjustTimeRangeForMinInterval(newValue);
		setTimeRange(adjustedValue as [number, number]);
		setCurrentPage(1);
	}, []);

	useEffect(() => {
		const previousDebouncedTimeRange = previousDebouncedTimeRangeRef.current;
		const hasDebouncedRangeChanged =
			!previousDebouncedTimeRange ||
			previousDebouncedTimeRange[0] !== debouncedTimeRange[0] ||
			previousDebouncedTimeRange[1] !== debouncedTimeRange[1];

		if (hasDebouncedRangeChanged) {
			setSelectedIndex(-1);
		}

		previousDebouncedTimeRangeRef.current = debouncedTimeRange;
	}, [debouncedTimeRange]);

	const handleDateApply = useCallback((date: DateValue | null) => {
		setSelectedDate(date);
		setSelectedIndex(-1);
		setCurrentPage(1);
	}, []);

	const handleTabChange = useCallback((key: Key | null) => {
		if (key) {
			setSelectedTab(key as "trips" | "positions");
			setSelectedIndex(-1);
			setCurrentPage(1);
		}
	}, []);

	const handleTripSelect = useCallback(
		async (tripId: number, index: number) => {
			setSelectedIndex(index);
			try {
				const coords = await getPoints({ id: tripId });
				const lastCoord = coords.at(-1);

				if (coords && coords.length > 0 && lastCoord) {
					const routeData: TripRouteData = {
						coordinates: coords,
						startMarker: {
							lat: coords[0].lat,
							lng: coords[0].lng,
							address: "",
							type: "start",
							showCar: true,
							speed: coords[0].speed,
							dateTelemetry: coords[0].dateTelemetry,
						},
						endMarker: {
							lat: lastCoord.lat,
							lng: lastCoord.lng,
							address: "",
							type: "end",
							speed: lastCoord.speed,
							dateTelemetry: lastCoord.dateTelemetry,
						},
					};
					onRouteSelect?.(routeData);
				}
			} catch {
				// silent fail
			}
		},
		[getPoints, onRouteSelect]
	);

	const trips = useMemo(() => tripsData?.items ?? [], [tripsData]);

	const handleSelectFirstTrip = useCallback(() => {
		if (
			isTripsMode &&
			!isFetchingTrips &&
			trips.length > 0 &&
			selectedIndex === -1
		) {
			if (hasInitialTripIds) {
				const idsSet = new Set(initialTripIds);
				const prioritizedTrip = trips.find((trip) => idsSet.has(trip.id));
				if (prioritizedTrip) {
					const prioritizedIndex = trips.findIndex(
						(trip) => trip.id === prioritizedTrip.id
					);
					handleTripSelect(prioritizedTrip.id, prioritizedIndex);
					return;
				}
			}
			handleTripSelect(trips[0].id, 0);
		}
	}, [
		isTripsMode,
		isFetchingTrips,
		trips,
		selectedIndex,
		handleTripSelect,
		hasInitialTripIds,
		initialTripIds,
	]);

	useEffect(() => {
		handleSelectFirstTrip();
	}, [handleSelectFirstTrip]);

	const isLoading = isTripsMode && isLoadingTrips;
	const items = isTripsMode ? trips : [];
	const hasNoData = isTripsMode && !isLoading && items.length === 0;
	const totalCount = isTripsMode ? tripsData?.totalCount : undefined;
	const totalPages = isTripsMode ? tripsData?.totalPages : undefined;

	const handlePreviousPage = useCallback(() => {
		setCurrentPage((prev) => Math.max(1, prev - 1));
	}, []);
	const handleNextPage = useCallback(() => {
		setCurrentPage((prev) => Math.min(totalPages ?? 1, prev + 1));
	}, [totalPages]);
	const handlePageClick = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const paginationPages = useMemo(
		() =>
			getTrackingPaginationItems({
				currentPage,
				totalPages: totalPages ?? 0,
			}),
		[currentPage, totalPages]
	);

	const lastUpdate = useMemo(
		() =>
			`${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString(
				"pt-BR",
				{
					hour: "2-digit",
					minute: "2-digit",
				}
			)}`,
		[]
	);

	const handleReplayReady = useCallback(
		(points: RouteCoordinate[]) => {
			onRouteSelect?.({ coordinates: points, isReplay: true });
		},
		[onRouteSelect]
	);

	const renderListContent = () => {
		if (!isTripsMode) {
			return (
				<PositionMetricsView
					finalDate={metricsFinalDate}
					initialDate={metricsInitialDate}
					lastUpdate={lastUpdate}
					onReplayReady={handleReplayReady}
					vehicleId={vehicleId}
				/>
			);
		}

		if (isLoading) {
			return <LoadingState />;
		}

		if (hasNoData) {
			return <EmptyState />;
		}

		if (items.length === 0) {
			return null;
		}

		return (
			<TripsList
				onSelect={setSelectedIndex}
				onTripClick={handleTripSelect}
				selectedIndex={selectedIndex}
				trips={trips}
			/>
		);
	};

	const renderPagination = () => {
		if (
			!isTripsMode ||
			isLoading ||
			hasNoData ||
			items.length === 0 ||
			(totalPages ?? 0) <= 1
		) {
			return null;
		}

		return (
			<div className="flex items-center justify-center gap-1 p-4">
				<Button
					className="h-8 w-8 p-0"
					disabled={currentPage <= 1}
					onClick={handlePreviousPage}
					size="sm"
					variant="outline"
				>
					&lt;
				</Button>
				{paginationPages.map((item, index) =>
					item.type === "page" ? (
						<Button
							className="h-8 w-8 p-0"
							key={`${item.type}-${item.value}-${index}`}
							onClick={() => handlePageClick(item.value)}
							size="sm"
							variant={item.isCurrent ? "default" : "outline"}
						>
							{item.value}
						</Button>
					) : (
						<PaginationEllipsisDropdown
							endPage={item.endPage}
							key={`${item.type}-${index}`}
							onPageChange={handlePageClick}
							startPage={item.startPage}
						/>
					)
				)}
				<Button
					className="h-8 w-8 p-0"
					disabled={currentPage >= (totalPages ?? 0)}
					onClick={handleNextPage}
					size="sm"
					variant="outline"
				>
					&gt;
				</Button>
			</div>
		);
	};

	return (
		<SideModal
			footer={renderPagination()}
			isOpen={isOpen}
			onClose={onClose}
			title="Posições e Viagens"
		>
			<div className="flex flex-col gap-6">
				<div className="flex items-center gap-2">
					<div className="h-2 w-2 rounded-full bg-[#17B26A]" />
					<span className="font-medium text-[#414651] text-sm">
						{vehiclePlate}
					</span>
				</div>

				<div className="flex items-end gap-3">
					<div className="flex-1">
						<DatePicker
							maxValue={today(getLocalTimeZone())}
							onApply={handleDateApply}
							popoverOffset={30}
							popoverPlacement="left"
							showTodayButton={false}
							value={selectedDate}
						/>
					</div>
					<div className="flex-1">
						<Select
							items={FILTER_OPTIONS}
							onSelectionChange={handleTabChange}
							placeholder="Selecione"
							selectedKey={selectedTab}
						>
							{(item) => <SelectItem {...item} />}
						</Select>
					</div>
				</div>

				<div className="flex flex-col gap-6">
					<span className="flex justify-between font-medium text-[#344054] text-sm">
						Filtrar horário
						<span className="text-[#667085] text-xs">
							Intervalo mínimo de 5 minutos
						</span>
					</span>
					<Slider
						className="w-full"
						labelFormatter={formatHour}
						labelPosition="top-floating"
						maxValue={23.99}
						minValue={0}
						onChange={handleTimeRangeChange}
						outputClassName="text-[10px] px-1 py-0.5"
						step={0.01}
						value={timeRange}
					/>
				</div>

				{isTripsMode ? (
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Route size={16} />
							<p className="font-medium text-[#344054] text-sm">
								{totalCount || 0} viagens
							</p>
						</div>
						<p className="text-[#667085] text-xs">
							Última atualização: {lastUpdate}
						</p>
					</div>
				) : null}

				<div className="min-h-[400px]">{renderListContent()}</div>
			</div>
		</SideModal>
	);
};
